import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

const features = [
  {
    icon: 'ri-timer-flash-line',
    tag: '業界初',
    title: '48時間以内着工保障',
    subtitle: '業界の常識を覆す圧倒的スピード',
    description:
      '待てるのは、せいぜい2日。だから私たちは48時間以内に伺います。「48時間」それは、お客様が安心を取り戻すまでの時間。リフォームの『めんどくさい』を、48時間でゼロにする。地元だからできる、48時間の約束。',
    points: [
      'LINEで写真を送るだけで即見積もり',
      '最短翌日から着工可能',
      '工期は最短1日で完了',
    ],
    highlight: true,
  },
  {
    icon: 'ri-map-pin-2-line',
    tag: 'LOCAL',
    title: '商圏30分限定',
    subtitle: '移動を削り、現場対応・顧客フォローに集中',
    description:
      '対応エリアをあえて30分圏内に絞ることで、迅速な現場対応と丁寧な顧客フォローを実現。地元密着だからこそできる、顔の見えるサービスをお届けします。',
    points: [
      '緊急トラブルにも迅速に駆けつけ',
      '工事後のアフターフォローも充実',
      '地域をよく知るプロが対応',
    ],
    highlight: false,
  },
  {
    icon: 'ri-price-tag-3-line',
    tag: 'CLEAR',
    title: '明朗会計・パック料金',
    subtitle: '追加費用の不安を一切排除',
    description:
      '見積もり額がそのまま最終金額。複雑な原価計算や後から発生する追加費用は一切ありません。すべて込みのパック料金で、安心してご検討いただけます。',
    points: [
      '追加費用の心配ゼロ',
      'わかりやすいパック料金',
      '見積もりは完全無料',
    ],
    highlight: false,
  },
];

export default function FeaturesSection() {
  const { ref, isVisible } = useIntersectionObserver(0.1);

  return (
    <section id="features" className="py-20 md:py-28 bg-white">
      <div
        ref={ref}
        className="max-w-7xl mx-auto px-4 md:px-8"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        <div className="text-center mb-14">
          <span className="inline-block text-accent font-display font-bold text-xs tracking-widest uppercase mb-3">
            Our Promise
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-navy leading-tight mb-4">
            選ばれる3つの理由
          </h2>
          <div className="w-16 h-1 bg-accent mx-auto rounded-full"></div>
          <p className="mt-4 text-gray-500 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            待てるのは、せいぜい2日。だから私たちは48時間以内に伺います。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className={`relative rounded-2xl p-8 md:p-10 border ${
                feature.highlight
                  ? 'bg-navy text-white border-navy'
                  : 'bg-white text-navy border-gray-100'
              }`}
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.7s ease ${index * 0.15}s, transform 0.7s ease ${index * 0.15}s`,
              }}
            >
              {feature.tag && (
                <span className="absolute top-6 right-6 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full">
                  {feature.tag}
                </span>
              )}

              <div
                className={`w-14 h-14 flex items-center justify-center rounded-xl mb-5 ${
                  feature.highlight ? 'bg-white/10' : 'bg-navy/5'
                }`}
              >
                <i
                  className={`${feature.icon} text-2xl ${
                    feature.highlight ? 'text-accent' : 'text-navy'
                  }`}
                ></i>
              </div>

              <h3
                className={`font-black text-2xl md:text-3xl mb-2 leading-tight ${
                  feature.highlight ? 'text-white' : 'text-navy'
                }`}
              >
                {feature.title}
              </h3>
              <p className="text-sm font-medium mb-4 text-accent">
                {feature.subtitle}
              </p>
              <p
                className={`text-sm leading-relaxed mb-6 ${
                  feature.highlight ? 'text-white/80' : 'text-gray-500'
                }`}
              >
                {feature.description}
              </p>

              <ul className="space-y-2">
                {feature.points.map((point) => (
                  <li
                    key={point}
                    className={`flex items-center gap-2 text-sm ${
                      feature.highlight ? 'text-white/90' : 'text-gray-600'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-accent text-base"></i>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          className="mt-10 rounded-2xl bg-accent/5 border border-accent/20 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.5s',
          }}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent/10 flex-shrink-0">
              <i className="ri-time-line text-accent text-xl"></i>
            </div>
            <div>
              <p className="font-bold text-navy text-base md:text-lg">業界平均 <strong className="text-gray-400 line-through">2週間</strong> → 私たちは <strong className="text-accent">48時間以内</strong></p>
              <p className="text-gray-500 text-sm">お申し込みから着工まで、圧倒的なスピードをお約束します。</p>
            </div>
          </div>
          <button
            onClick={() => {
              const el = document.querySelector('#contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="flex items-center gap-2 bg-accent hover:bg-accent-dark text-white px-6 py-3 rounded-full font-bold text-sm whitespace-nowrap transition-all duration-300 cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-line-fill text-base"></i>
            </span>
            今すぐ相談する
          </button>
        </div>
      </div>
    </section>
  );
}
